// Derived state for an analyst tab (A2 / A3 / A4).
//
// Section 3 (Lane Pass/Fail) is locked until:
//   - Section 1: every catalog item in the lane is terminal (Approved /
//     Declined / Waived). An empty lane (no items) counts as not-ready.
//   - Section 2: every cert in the lane's template list has reached a
//     terminal status (Certified / Declined / Escalated).

import { useMemo } from "react";
import {
  useCertificationTemplatesByLane,
  useCriteriaChecklistByLane,
  useDealAssignments,
  useLaneAssessment,
  useUnderwritingCertifications,
} from "@/hooks/dealFlow/useDealFlow";
import type {
  CriteriaChecklistItemDto,
  CriteriaLane,
  DealAssignmentDto,
} from "@/api/dealFlow/types";
import { tabForAnalystLane } from "../AdditionalCertifications/certConfigs";
import { unresolvedSectionTwoCodes } from "./analystSectionTwo";

export interface AnalystTabState {
  /** Number of Section 1 items not yet in terminal status. */
  pendingItemCount: number;
  totalItemCount: number;
  section1Complete: boolean;
  /** Number of Section 2 certs not yet signed. */
  pendingCertCount: number;
  totalCertCount: number;
  section2Complete: boolean;
  /** True once a LaneAssessment exists for this lane. */
  section3Complete: boolean;
  /** Combined gate; true once Section 3 may be issued. */
  canIssueLaneAssessment: boolean;
  /** Tooltip copy explaining what still blocks Section 3, when locked. */
  blockingReason: string;
  /** True if the lane's DealAssignment status === NotApplicable. */
  isNotApplicable: boolean;
  notApplicableAt: string | null;
  notApplicableByUserId: string | null;
  /** Most recent mutation timestamp across S1/S2/S3 (footer copy). */
  lastUpdatedAt: string | null;
  lastUpdatedByUserId: string | null;
  isLoading: boolean;
}

export function useAnalystTabState(
  dealId: string | undefined,
  lane: CriteriaLane,
): AnalystTabState {
  const assignments = useDealAssignments(dealId);
  const items = useCriteriaChecklistByLane(dealId, lane);
  const tab = tabForAnalystLane(lane);
  const certs = useUnderwritingCertifications(dealId, tab, 2);
  const laneAssessment = useLaneAssessment(dealId, lane);
  // The cert universe is the DB template list (admin-editable), same source the
  // Section 2 list renders from — so the gate counts can never drift from it.
  const certTemplates = useCertificationTemplatesByLane(lane);

  return useMemo<AnalystTabState>(() => {
    const isLoading =
      assignments.isLoading ||
      items.isLoading ||
      certs.isLoading ||
      certTemplates.isLoading ||
      laneAssessment.isLoading;

    // Section 2's templates, as the server reads them for the lane issue.
    const requiredCerts = (certTemplates.data ?? []).filter((t) => t.section === 2);

    const assignment: DealAssignmentDto | undefined = (assignments.data ?? [])
      .find((a) => a.roleCode === lane);
    const isNotApplicable = assignment?.status === "NotApplicable";

    const itemList: CriteriaChecklistItemDto[] = items.data ?? [];
    const totalItemCount = itemList.length;
    const pendingItemCount = itemList.filter(
      (i) =>
        i.status !== "Approved" &&
        i.status !== "Declined" &&
        i.status !== "Waived",
    ).length;
    const section1Complete = totalItemCount > 0 && pendingItemCount === 0;

    // The server's lane-issue rule (analystSectionTwo.ts, pinned to its C#
    // copy by a shared fixture).
    const totalCertCount = requiredCerts.length;
    const pendingCertCount = unresolvedSectionTwoCodes(
      requiredCerts.map((c) => c.certificationCode),
      certs.data ?? [],
    ).length;
    const section2Complete = pendingCertCount === 0;

    const section3Complete = !!laneAssessment.data;
    const canIssueLaneAssessment =
      section1Complete && section2Complete && !section3Complete;

    const reasonParts: string[] = [];
    if (!section1Complete) {
      reasonParts.push(
        `${pendingItemCount} of ${totalItemCount} checklist items pending`,
      );
    }
    if (!section2Complete) {
      reasonParts.push(
        `${pendingCertCount} of ${totalCertCount} certifications unsigned`,
      );
    }
    const blockingReason =
      reasonParts.length === 0
        ? section3Complete
          ? "Lane assessment already issued."
          : "Ready to issue."
        : reasonParts.join("; ");

    // Footer "Last updated" — take the most recent timestamp across the
    // three sections we know about. Fall back to null if no activity.
    const candidates: Array<{
      at: string | null;
      by: string | null;
    }> = [];
    for (const i of itemList) {
      candidates.push({
        at: i.certifiedAt ?? i.escalatedAt ?? null,
        by: i.certifiedByUserId ?? i.escalatedToUserId ?? i.waivedByUserId,
      });
    }
    for (const c of certs.data ?? []) {
      // Take the most recent terminal timestamp on the
      // cert row (certified / declined / escalated), falling back to the
      // legacy signedAt for any rows still written by the Sign ceremony.
      candidates.push({
        at: c.certifiedAt ?? c.declinedAt ?? c.escalatedAt ?? c.signedAt,
        by:
          c.certifiedByUserId ??
          c.declinedByUserId ??
          c.escalatedByUserId ??
          c.signedByUserId,
      });
    }
    if (laneAssessment.data) {
      candidates.push({
        at: laneAssessment.data.assessedAt,
        by: laneAssessment.data.assessedByUserId,
      });
    }
    const sorted = candidates
      .filter((c): c is { at: string; by: string | null } => !!c.at)
      .sort((a, b) => (a.at < b.at ? 1 : -1));
    const top = sorted[0];

    return {
      pendingItemCount,
      totalItemCount,
      section1Complete,
      pendingCertCount,
      totalCertCount,
      section2Complete,
      section3Complete,
      canIssueLaneAssessment,
      blockingReason,
      isNotApplicable,
      notApplicableAt: isNotApplicable ? (assignment?.assignedAt ?? null) : null,
      notApplicableByUserId: isNotApplicable
        ? (assignment?.assignedByUserId ?? null)
        : null,
      lastUpdatedAt: top?.at ?? null,
      lastUpdatedByUserId: top?.by ?? null,
      isLoading,
    };
  }, [
    assignments.data,
    assignments.isLoading,
    items.data,
    items.isLoading,
    certs.data,
    certs.isLoading,
    certTemplates.data,
    certTemplates.isLoading,
    laneAssessment.data,
    laneAssessment.isLoading,
    lane,
  ]);
}
